import { CANONICAL_ASSET_ORIGIN, isConsenteerHost } from "./origin.js";

/**
 * Resolve an asset URL against the SDK script's origin.
 *
 * Visitor pages run on their own domain (e.g. example.com) but the
 * Consenteer-hosted logos live on the saas-api origin. A naive
 * `<img src="/logo/icon.png">` would resolve against example.com and
 * 404 in production. By rewriting relative paths to the script's
 * origin, the same default path (`/logo/consenteer_icon_350.png`)
 * works in dev (where the script is served from localhost:3001) and
 * in prod (saas.consenteer.com) without any per-environment config.
 *
 * Absolute URLs (https?://) are passed through unchanged so site
 * owners can host their own custom icon.
 *
 * When the bundle is a copy re-hosted on the customer's domain (cf. origin.ts),
 * its origin holds none of our assets: fall back to the canonical CDN, or the
 * reopen icon and the fonts would 404 on the customer's site.
 */
export function resolveAssetUrl(path: string | undefined | null): string {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (typeof document === "undefined") return path;

  const script =
    (document.getElementById("csteer") as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>("script[data-site]");
  if (!script?.src) return path;

  try {
    const src = new URL(script.src);
    const base = isConsenteerHost(src.hostname) ? src.origin : CANONICAL_ASSET_ORIGIN;
    return new URL(path, base).toString();
  } catch {
    return path;
  }
}
